// Button — primary actions across the app. Variants:
//   primary     — slate-900 / white. The default. Used for "Submit", "New
//                 facility", "Sign in", "Ask AI". This is THE button color
//                 from the mockups.
//   secondary   — white / slate-900 with stone border. Used for "Filter",
//                 cancel, alternate actions.
//   destructive — st-danger-solid / white. Used for "Delete", "Reject" (rare).
//                 NOT rag-red: that token means facility health, and when CW-J
//                 darkened it to #C0271C this button's hover (#DC2626) became
//                 LIGHTER than its base — the hover read as a disabled state.
//   ghost       — transparent / slate-700, hover stone-100. Used for
//                 inline tertiary actions ("Load all", "Cancel").
//
// Sizes:
//   md (default) — 44px tall on mobile (h-11), good thumb target
//   sm           — 36px tall (h-9), denser desktop interactions
//
// Polymorphic `as` prop so it can render as <a> or react-router <Link> when
// the button-shaped element is actually a navigation. Default is <button>.
// Renders via React.createElement so TypeScript stays happy with the
// dynamic element type without juggling generics.

import {
  createElement,
  forwardRef,
  type ElementType,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner before the label. Disables the button when true. */
  loading?: boolean;
  disabled?: boolean;
  /** Optional icon rendered before the label (or alone if no children). */
  icon?: ReactNode;
  /** Render as a different element. Useful for Link / anchor. */
  as?: ElementType;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLElement>;
  // Pass-through props — explicitly listed so the type stays narrow.
  href?: string;
  to?: string; // react-router Link
  target?: string;
  rel?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-haspopup'?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-tcs-inverse text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:bg-tcs-text-muted',
  secondary:
    'bg-tcs-bg text-tcs-text border border-tcs-border-strong hover:bg-tcs-bg-muted disabled:opacity-50',
  destructive:
    'bg-st-danger-solid text-st-solid-ink hover:bg-st-danger-fg-strong disabled:bg-st-danger-border-strong',
  ghost:
    'text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text disabled:opacity-50',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'h-11 px-5 text-sm',
  sm: 'h-9 px-3.5 text-xs',
};

export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    loading,
    disabled,
    icon,
    as = 'button',
    className,
    type,
    ...rest
  },
  ref,
) {
  const cls = [
    'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed',
    SIZE_CLASSES[size],
    VARIANT_CLASSES[variant],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const isDisabled = disabled || loading;
  const elementProps =
    as === 'button'
      ? { type: type ?? 'button', disabled: isDisabled }
      : {};
  return createElement(
    as as ElementType,
    { ref, className: cls, ...elementProps, ...rest },
    loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon,
    children,
  );
});
